import { DataTablePagination } from '@easydoctor/ui';
import { ThemeProvider, type Theme } from '@mui/material/styles';
import type { TablePaginationProps } from '@mui/material/TablePagination';

/** Nomi accessibili in italiano dei pulsanti della paginazione: MUI li ha in inglese («Go to next page»). */
const getItemAriaLabel: NonNullable<TablePaginationProps['getItemAriaLabel']> = (type) =>
  ({ first: 'Prima pagina', last: 'Ultima pagina', next: 'Pagina successiva', previous: 'Pagina precedente' })[type];

/**
 * Il tema di fuori con i nomi italiani come default di `TablePagination`. `DataTablePagination` non
 * espone `getItemAriaLabel`: finché il pacchetto non li porta (richiesta in easycare#1740), li mette il
 * tema, solo qui.
 */
function withItalianLabels(outer: Theme): Theme {
  const components = outer.components ?? {};
  return {
    ...outer,
    components: {
      ...components,
      MuiTablePagination: {
        ...components.MuiTablePagination,
        defaultProps: { ...components.MuiTablePagination?.defaultProps, getItemAriaLabel },
      },
    },
  };
}

interface QueuePaginationProps {
  total: number;
  /** Pagina corrente, da 1 come nell'URL. */
  page: number;
  pageSize: number;
  /** Pagina scelta, da 1. */
  onPageChange: (page: number) => void;
}

/**
 * Paginazione della coda (#105): `DataTablePagination` di `@easydoctor/ui` come in Pazienti, con
 * «Mostrando 1-50 di 120» e i salti alla prima e all'ultima pagina. Pagine da 1 come nell'URL (`page`),
 * da 0 per il componente; nessun «Righe per pagina»: la coda resta a `QUEUE_PAGE_SIZE_DEFAULT`.
 */
export function QueuePagination({ total, page, pageSize, onPageChange }: QueuePaginationProps) {
  return (
    <ThemeProvider theme={withItalianLabels}>
      <DataTablePagination
        totalCount={total}
        page={page - 1}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[pageSize]}
        onPageChange={(next) => onPageChange(next + 1)}
        showFirstButton
        showLastButton
      />
    </ThemeProvider>
  );
}
